import { createContext, useContext, useEffect, useState } from 'react';
import { territoriesService } from '@/services/territories';
import { contextsService } from '@/services/contexts';
import { topicsService } from '@/services/topics';
import { partnerService } from '@/services/partner';
import { sourcesService } from '@/services/sources';
import { Territory } from '@/types/territorios';
import { Context } from '@/types/contexts';
import { Topic } from '@/types/topics';
import { Partner } from '@/types/partner';
import { Source } from '@/types/sources';
import { useAuth } from './auth';

interface DataContextValue {
  territories: Territory[];
  territoriesLoading: boolean;
  territoriesError: string | null;
  contexts: Context[];
  contextsLoading: boolean;
  contextsError: string | null;
  topics: Topic[];
  topicsLoading: boolean;
  topicsError: string | null;
  partners: Partner[];
  partnersLoading: boolean;
  partnersError: string | null;
  sources: Source[];
  sourcesLoading: boolean;
  sourcesError: string | null;
}

const DataContext = createContext<DataContextValue | null>(null);

let territoriesRequest: Promise<{ data: Territory[] }> | null = null;
let contextsRequest: Promise<{ data: Context[] }> | null = null;
let topicsRequest: Promise<{ data: Topic[] }> | null = null;
let partnersRequest: Promise<{ data: Partner[] }> | null = null;
let sourcesRequest: Promise<{ data: Source[] }> | null = null;

function getTerritoriesOnce() {
  if (!territoriesRequest) territoriesRequest = territoriesService.getAll();
  return territoriesRequest;
}

function getContextsOnce() {
  if (!contextsRequest) contextsRequest = contextsService.getAll({ offset: 0, limit: 1000 });
  return contextsRequest;
}

function getTopicsOnce() {
  if (!topicsRequest) topicsRequest = topicsService.getAll();
  return topicsRequest;
}

function getPartnersOnce() {
  if (!partnersRequest) partnersRequest = partnerService.getAll({ offset: 0, limit: 1000 });
  return partnersRequest;
}

function getSourcesOnce() {
  if (!sourcesRequest) sourcesRequest = sourcesService.getAll({ offset: 0, limit: 1000 });
  return sourcesRequest;
}

export function DataProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(true);
  const [territoriesError, setTerritoriesError] = useState<string | null>(null);
  const [contexts, setContexts] = useState<Context[]>([]);
  const [contextsLoading, setContextsLoading] = useState(true);
  const [contextsError, setContextsError] = useState<string | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [partnersError, setPartnersError] = useState<string | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [sourcesError, setSourcesError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setTerritories([]);
      setContexts([]);
      setTopics([]);
      setPartners([]);
      setSources([]);
      setTerritoriesLoading(false);
      setContextsLoading(false);
      setTopicsLoading(false);
      setPartnersLoading(false);
      setSourcesLoading(false);
      setTerritoriesError(null);
      setContextsError(null);
      setTopicsError(null);
      setPartnersError(null);
      setSourcesError(null);
      territoriesRequest = null;
      contextsRequest = null;
      topicsRequest = null;
      partnersRequest = null;
      sourcesRequest = null;
      return;
    }
    let cancelled = false;
    getTerritoriesOnce()
      .then((res) => { if (!cancelled) { setTerritories(res.data ?? []); setTerritoriesError(null); } })
      .catch(() => { if (!cancelled) setTerritoriesError('Failed to load territories'); })
      .finally(() => { if (!cancelled) setTerritoriesLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getContextsOnce()
      .then((res) => { if (!cancelled) { setContexts(res.data ?? []); setContextsError(null); } })
      .catch(() => { if (!cancelled) setContextsError('Failed to load contexts'); })
      .finally(() => { if (!cancelled) setContextsLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getTopicsOnce()
      .then((res) => { if (!cancelled) { setTopics(res.data ?? []); setTopicsError(null); } })
      .catch(() => { if (!cancelled) setTopicsError('Failed to load topics'); })
      .finally(() => { if (!cancelled) setTopicsLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getPartnersOnce()
      .then((res) => { if (!cancelled) { setPartners(res.data ?? []); setPartnersError(null); } })
      .catch(() => { if (!cancelled) setPartnersError('Failed to load partners'); })
      .finally(() => { if (!cancelled) setPartnersLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getSourcesOnce()
      .then((res) => { if (!cancelled) { setSources(res.data ?? []); setSourcesError(null); } })
      .catch(() => { if (!cancelled) setSourcesError('Failed to load sources'); })
      .finally(() => { if (!cancelled) setSourcesLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const value: DataContextValue = {
    territories,
    territoriesLoading,
    territoriesError,
    contexts,
    contextsLoading,
    contextsError,
    topics,
    topicsLoading,
    topicsError,
    partners,
    partnersLoading,
    partnersError,
    sources,
    sourcesLoading,
    sourcesError,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within a DataProvider');
  return ctx;
}
